import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CloudUpload as CloudUploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import { userFrameService, UserFrame } from '../../services/userFrameService';
import { useI18n } from '../../i18n/useI18n';
import CustomFrameLayoutPreview from '../shared/CustomFrameLayoutPreview';
import { userCardEnterSx } from '../shared/UserPageShell';
import { rewriteToCdn } from '../../utils/cdn';

export type UserCustomFrameChannel = 'pro' | 'lite';

const FRAME_SIZE_PRIORITY = ['2x4', '4x2', '2x6', '6x2', '4x6', '6x4', '3x4', '4x3'];

const normalizeFrameSize = (sizeValue?: string) =>
  String(sizeValue || '').trim().toLowerCase();

interface UserChannelCustomFramesProps {
  channel: UserCustomFrameChannel;
  /** Admin đang xem theme của user này. Pro và Lite dùng API riêng theo channel. */
  ownerUserId?: string;
  embedded?: boolean;
  showIntro?: boolean;
  viewInDialog?: boolean;
}

const toDialogUserFrame = (item: api.AdminUserCustomFrameAsset): UserFrame => ({
  _id: item._id,
  customFrameId: item.customFrameId,
  userId: item.userId,
  name: item.name,
  description: item.description || '',
  frameImage: {
    url: item.frameImage?.url || '',
    path: item.frameImage?.path || '',
  },
  isActive: item.isActive,
  tags: item.tags || [],
  metadata: {
    originalFileName: item.metadata?.originalFileName || '',
    fileSize: item.metadata?.fileSize || 0,
    uploadDate: item.metadata?.uploadDate || item.createdAt,
  },
  customFrame: {
    _id: item.customFrameId,
    name: '',
    size: '',
    photoPositions: [],
  },
  createdAt: item.createdAt,
  updatedAt: item.updatedAt,
});

const UserChannelCustomFrames: React.FC<UserChannelCustomFramesProps> = ({
  channel,
  ownerUserId,
  embedded = true,
  showIntro = true,
  viewInDialog = false,
}) => {
  const { pages: t } = useI18n();
  const navigate = useNavigate();
  const [grantedFrames, setGrantedFrames] = useState<api.CustomFrame[]>([]);
  const [enabledFrameIds, setEnabledFrameIds] = useState<Set<string>>(new Set());
  const [selectedFrameSizes, setSelectedFrameSizes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [userFrameDialogFrame, setUserFrameDialogFrame] = useState<api.CustomFrame | null>(null);
  const [userFrames, setUserFrames] = useState<UserFrame[]>([]);
  const [userFramesLoading, setUserFramesLoading] = useState(false);
  const [userFrameUploading, setUserFrameUploading] = useState(false);
  const [deletingUserFrameId, setDeletingUserFrameId] = useState<string | null>(null);
  const userFrameFileInputRef = useRef<HTMLInputElement | null>(null);

  const detailPath = useCallback(
    (frameId: string) =>
      channel === 'lite'
        ? `/m-photo-lite/custom-frames/${frameId}`
        : `/user/custom-frames/${frameId}`,
    [channel]
  );

  const copy = useMemo(() => {
    if (channel === 'pro') {
      return { title: t.customFrames.titlePro, hint: t.customFrames.hintPro };
    }
    return { title: t.customFrames.titleLite, hint: t.customFrames.hintLite };
  }, [channel, t]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = ownerUserId
        ? await api.getUserCustomFramesChannelGranted(ownerUserId, channel)
        : await api.getMyCustomFramesChannelGranted(channel);
      setGrantedFrames(data.grantedFrames || []);
      setEnabledFrameIds(new Set((data.enabledFrameIds || []).map((id) => String(id))));
    } catch {
      toast.error('Không thể tải custom frames');
    } finally {
      setLoading(false);
    }
  }, [channel, ownerUserId]);

  useEffect(() => {
    load();
  }, [load]);

  const openUserFrames = async (frame: api.CustomFrame) => {
    if (!viewInDialog) {
      navigate(detailPath(frame._id));
      return;
    }
    setUserFrameDialogFrame(frame);
    setUserFrames([]);
    setUserFramesLoading(true);
    try {
      if (ownerUserId) {
        const assets = await api.getAdminUserCustomFrameAssets(
          ownerUserId,
          frame._id,
          'user_frame',
          1,
          200,
          channel,
        );
        setUserFrames((assets.items || []).map(toDialogUserFrame));
      } else {
        const response = await userFrameService.getUserFrames({
          customFrameId: frame._id,
          limit: 100,
          channel,
        });
        setUserFrames(response.userFrames || []);
      }
    } catch {
      toast.error('Không thể tải user frame');
      setUserFrames([]);
    } finally {
      setUserFramesLoading(false);
    }
  };

  const uploadDialogUserFrames = async (picked: File[]) => {
    if (!userFrameDialogFrame || picked.length === 0) return;
    const files = picked.slice(0, 10);
    for (const file of files) {
      const isImage = file.type.startsWith('image/') || /\.(png|jpe?g)$/i.test(file.name);
      if (!isImage) {
        toast.error(`File ${file.name} không phải là file ảnh (JPEG, PNG)`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`File ${file.name} vượt quá giới hạn 5MB`);
        return;
      }
    }
    try {
      setUserFrameUploading(true);
      const created = ownerUserId
        ? (await api.uploadAdminUserCustomFrameAssets(
            ownerUserId,
            userFrameDialogFrame._id,
            'user_frame',
            files,
            channel,
          )).items.map(toDialogUserFrame)
        : ((await userFrameService.uploadUserFrames(userFrameDialogFrame._id, files, channel)).createdFrames || []) as UserFrame[];
      if (created.length > 0) {
        setUserFrames((prev) => [...created, ...prev]);
      }
      toast.success(`Đã thêm ${created.length} user frame`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Upload user frame thất bại');
    } finally {
      setUserFrameUploading(false);
    }
  };

  const deleteDialogUserFrame = async (userFrameId: string) => {
    try {
      setDeletingUserFrameId(userFrameId);
      if (ownerUserId && userFrameDialogFrame) {
        await api.deleteAdminUserCustomFrameAsset(
          ownerUserId,
          userFrameDialogFrame._id,
          userFrameId,
          'user_frame',
          channel,
        );
      } else {
        await userFrameService.deleteUserFrame(userFrameId, channel);
      }
      setUserFrames((prev) => prev.filter((frame) => frame._id !== userFrameId));
      toast.success('Đã xóa user frame');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Không xóa được user frame');
    } finally {
      setDeletingUserFrameId(null);
    }
  };

  const enabledFrames = useMemo(
    () => grantedFrames.filter((frame) => enabledFrameIds.has(String(frame._id))),
    [grantedFrames, enabledFrameIds],
  );

  const orderedSizes = useMemo(() => {
    const availableSizes = new Set(
      enabledFrames.map((f) => normalizeFrameSize(f.size)).filter(Boolean),
    );
    const ordered = FRAME_SIZE_PRIORITY.map((s) => normalizeFrameSize(s));
    const rest = Array.from(availableSizes).filter((s) => !ordered.includes(s)).sort();
    return [...ordered, ...rest];
  }, [enabledFrames]);

  const visibleFrames = useMemo(() => {
    let list = [...enabledFrames];
    if (selectedFrameSizes.length > 0) {
      const set = new Set(selectedFrameSizes.map(normalizeFrameSize));
      list = list.filter((f) => set.has(normalizeFrameSize(f.size)));
    }
    const priorityMap = new Map(FRAME_SIZE_PRIORITY.map((s, i) => [normalizeFrameSize(s), i]));
    return list.sort((a, b) => {
      const ap = priorityMap.get(normalizeFrameSize(a.size)) ?? 999;
      const bp = priorityMap.get(normalizeFrameSize(b.size)) ?? 999;
      if (ap !== bp) return ap - bp;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [enabledFrames, selectedFrameSizes]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {!embedded && (
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {copy.title}
        </Typography>
      )}
      {embedded && showIntro && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} color="#e86a8a" gutterBottom>
            {copy.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {copy.hint}
          </Typography>
        </Box>
      )}

      {enabledFrames.length === 0 ? (
        <Alert severity="info">
          {t.customFrames.emptyEnabled}
        </Alert>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              mb: 2,
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: alpha('#ff8ba7', 0.28),
              background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha('#ff8ba7', 0.06)} 100%)`,
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 1, color: '#e86a8a' }}
            >
              {t.customFrames.filterSize}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(5, minmax(0, 1fr))',
                  sm: 'repeat(6, minmax(0, 1fr))',
                  md: 'repeat(8, minmax(0, 1fr))',
                },
                gap: { xs: 0.6, sm: 1 },
              }}
            >
              <Button
                size="small"
                variant={selectedFrameSizes.length === 0 ? 'contained' : 'outlined'}
                onClick={() => setSelectedFrameSizes([])}
                sx={{
                  minWidth: 0,
                  px: { xs: 0.25, sm: 1 },
                  fontSize: { xs: '0.68rem', sm: '0.8125rem' },
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  ...(selectedFrameSizes.length === 0
                    ? {
                        bgcolor: '#e86a8a',
                        color: '#fff',
                        boxShadow: `0 2px 8px ${alpha('#e86a8a', 0.3)}`,
                        '&:hover': { bgcolor: alpha('#e86a8a', 0.88) },
                      }
                    : {
                        color: '#e86a8a',
                        borderColor: alpha('#ff8ba7', 0.45),
                        bgcolor: alpha('#fff', 0.8),
                        '&:hover': {
                          borderColor: '#e86a8a',
                          bgcolor: alpha('#ff8ba7', 0.12),
                        },
                      }),
                }}
              >
                {t.common.all}
              </Button>
              {orderedSizes.map((size) => {
                const selected = selectedFrameSizes.includes(size);
                return (
                  <Button
                    key={size}
                    size="small"
                    variant={selected ? 'contained' : 'outlined'}
                    onClick={() =>
                      setSelectedFrameSizes((prev) =>
                        prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size],
                      )
                    }
                    sx={{
                      minWidth: 0,
                      px: { xs: 0.25, sm: 1 },
                      fontSize: { xs: '0.68rem', sm: '0.8125rem' },
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      ...(selected
                        ? {
                            bgcolor: '#e86a8a',
                            color: '#fff',
                            boxShadow: `0 2px 8px ${alpha('#e86a8a', 0.3)}`,
                            '&:hover': { bgcolor: alpha('#e86a8a', 0.88) },
                          }
                        : {
                            color: '#e86a8a',
                            borderColor: alpha('#ff8ba7', 0.45),
                            bgcolor: alpha('#fff', 0.8),
                            '&:hover': {
                              borderColor: '#e86a8a',
                              bgcolor: alpha('#ff8ba7', 0.12),
                            },
                          }),
                    }}
                  >
                    {size}
                  </Button>
                );
              })}
            </Box>
          </Paper>

          <Grid container spacing={{ xs: 1.25, sm: 2 }}>
            {visibleFrames.map((frame, index) => {
              return (
                <Grid item xs={6} sm={6} md={4} key={frame._id}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.25, sm: 2 },
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2.5,
                      border: '1px solid',
                      borderColor: alpha('#ff8ba7', 0.28),
                      boxShadow: `0 4px 14px ${alpha('#ff8ba7', 0.08)}`,
                      background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha('#ff8ba7', 0.08)} 100%)`,
                      cursor: 'pointer',
                      overflow: 'visible',
                      ...userCardEnterSx(index),
                    }}
                    onClick={() => openUserFrames(frame)}
                  >
                    <CustomFrameLayoutPreview
                      width={frame.width}
                      height={frame.height}
                      size={frame.size}
                      photoPositions={frame.photoPositions}
                      maxHeight={{ xs: 200, sm: 240 }}
                    />
                    <Typography
                      fontWeight={700}
                      sx={{
                        color: '#e86a8a',
                        fontSize: { xs: '0.8rem', sm: '1rem' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mt: 0.75,
                        minHeight: { xs: '1.25rem', sm: '1.5rem' },
                      }}
                    >
                      {frame.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha('#e86a8a', 0.7),
                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                        minHeight: { xs: '1.05rem', sm: '1.3rem' },
                      }}
                    >
                      {frame.size} · {frame.maxPhotos} ảnh
                    </Typography>
                    <Box sx={{ mt: 0.75, minHeight: { xs: 28, sm: 32 } }}>
                      <Button
                        size="small"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          color: '#e86a8a',
                          fontSize: { xs: '0.72rem', sm: '0.8125rem' },
                          px: { xs: 0.5, sm: 1 },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openUserFrames(frame);
                        }}
                      >
                        {t.customFrames.viewUserFrames}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      <Dialog
        open={Boolean(userFrameDialogFrame)}
        onClose={(_event, reason) => {
          if (userFrameUploading) return;
          if (reason === 'backdropClick' && document.activeElement === userFrameFileInputRef.current) return;
          setUserFrameDialogFrame(null);
        }}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <DialogTitle sx={{ fontWeight: 800, pr: 2 }}>
          {t.customFrames.viewUserFrames}
          {userFrameDialogFrame ? ` — ${userFrameDialogFrame.name}` : ''}
        </DialogTitle>
        <DialogContent>
          <Paper
            elevation={0}
            onClick={() => {
              if (userFrameUploading || userFramesLoading || !userFrameDialogFrame) return;
              userFrameFileInputRef.current?.click();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              mb: 2,
              borderRadius: 2.5,
              border: '1.5px solid',
              borderColor: alpha('#ff8ba7', 0.35),
              background: `linear-gradient(135deg, ${alpha('#ff8ba7', 0.18)} 0%, ${alpha('#ff8ba7', 0.08)} 100%)`,
              cursor: userFrameUploading || userFramesLoading ? 'default' : 'pointer',
            }}
          >
            <input
              ref={userFrameFileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,.png,.jpg,.jpeg"
              multiple
              style={{ display: 'none' }}
              disabled={userFrameUploading || userFramesLoading || !userFrameDialogFrame}
              onChange={(e) => {
                const files = e.target.files ? Array.from(e.target.files) : [];
                e.target.value = '';
                uploadDialogUserFrames(files);
              }}
            />
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#ff8ba7', 0.35),
                color: '#e86a8a',
                flexShrink: 0,
              }}
            >
              {userFrameUploading ? <CircularProgress size={22} sx={{ color: '#e86a8a' }} /> : <CloudUploadIcon />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: '#e86a8a', fontSize: '0.95rem' }}>
                {userFrameUploading ? t.themes.pickUserFramesUploading : t.themes.pickUserFramesUpload}
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#e86a8a', 0.75) }}>
                {t.themes.pickUserFramesUploadHint}
              </Typography>
            </Box>
          </Paper>
          {userFramesLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : userFrames.length === 0 ? (
            <Alert severity="info">{t.themes.pickUserFramesEmpty}</Alert>
          ) : (
            <Grid container spacing={1.5}>
              {userFrames.map((frame) => {
                const imageUrl = frame.frameImage?.url ? rewriteToCdn(frame.frameImage.url) : '';
                const deleting = deletingUserFrameId === frame._id;
                return (
                  <Grid item xs={6} sm={4} key={frame._id}>
                    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deleting}
                        onClick={() => deleteDialogUserFrame(frame._id)}
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          zIndex: 1,
                          bgcolor: 'rgba(255,255,255,0.92)',
                          '&:hover': { bgcolor: 'error.main', color: 'white' },
                        }}
                        aria-label={t.common.delete}
                      >
                        {deleting ? <CircularProgress size={16} /> : <DeleteIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                      <CardMedia
                        image={imageUrl}
                        title={frame.name}
                        sx={{
                          height: 140,
                          bgcolor: alpha('#ff8ba7', 0.08),
                          backgroundSize: 'contain',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                        }}
                      />
                      <CardContent sx={{ py: 1, px: 1, '&:last-child': { pb: 1 } }}>
                        <Typography noWrap fontWeight={700} sx={{ fontSize: '0.8rem', textAlign: 'center' }}>
                          {frame.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (userFrameUploading) return;
              setUserFrameDialogFrame(null);
            }}
            disabled={userFrameUploading}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
          >
            {t.common.close}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserChannelCustomFrames;
